import React, { useState } from 'react';
import { useImageViewer } from './useImageViewer';
import { MediaService } from '@ianesthesie-kit/services';
import { useErrorDialog } from '@ianesthesie-kit/redux';
import { styled } from '@mui/material/styles';
import { IconButton, IconButtonProps } from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBackIosRounded';
import ForwardIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ZoomInIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutIcon from '@mui/icons-material/ZoomOutRounded';
import ResetZoomIcon from '@mui/icons-material/CenterFocusWeakRounded';
import RotateRightIcon from '@mui/icons-material/RotateRightRounded';
import RotateLeftIcon from '@mui/icons-material/RotateLeftRounded';
import DownloadIcon from '@mui/icons-material/CloudDownloadRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import PrintIcon from '@mui/icons-material/PrintRounded';
import CloseIcon from '@mui/icons-material/ClearRounded';
import { TrashIcon } from '@/components/icon';
import { ConfirmDelete } from '@/components/ConfirmDelete';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import { useDeleteCurrentPatientMedium } from '@/hooks';

const StyledIconButton = styled(IconButton)`
  transition: transform 0.2s 0.1s;
  transform: scale(1);
  :hover {
    transition-delay: 0s;
    transform: scale(1.2);
  }

  &.Mui-disabled {
    color: inherit;
    opacity: 0.5;
    transform: scale(0.75);
  }
`;

const BaseControlButton = (props: IconButtonProps) => (
  <StyledIconButton size="small" color="inherit" {...props} />
);

export const Prev = () => {
  const { hasPrevious, goToPrev } = useImageViewer();

  return (
    <BaseControlButton disabled={!hasPrevious} onClick={goToPrev}>
      <BackIcon />
    </BaseControlButton>
  );
};

export const Next = () => {
  const { hasNext, goToNext } = useImageViewer();

  return (
    <BaseControlButton disabled={!hasNext} onClick={goToNext}>
      <ForwardIcon />
    </BaseControlButton>
  );
};

export const ZoomIn = ({ disabled }: { disabled?: boolean }) => {
  const { canZoomIn, zoomIn } = useImageViewer();

  return (
    <BaseControlButton disabled={disabled || !canZoomIn} onClick={zoomIn}>
      <ZoomInIcon />
    </BaseControlButton>
  );
};

export const ZoomOut = ({ disabled }: { disabled?: boolean }) => {
  const { canZoomOut, zoomOut } = useImageViewer();

  return (
    <BaseControlButton disabled={disabled || !canZoomOut} onClick={zoomOut}>
      <ZoomOutIcon />
    </BaseControlButton>
  );
};

export const Reset = ({
  disabled,
  onReset
}: {
  disabled?: boolean;
  onReset?: () => void;
}) => {
  const { resetZoom } = useImageViewer();

  return (
    <BaseControlButton
      disabled={disabled}
      onClick={() => {
        resetZoom();
        onReset?.();
      }}
    >
      <ResetZoomIcon />
    </BaseControlButton>
  );
};

export const Rotate = ({
  type,
  disabled
}: {
  disabled?: boolean;
  type: 'left' | 'right';
}) => {
  const { rotateLeft, rotateRight } = useImageViewer();

  return (
    <BaseControlButton
      disabled={disabled}
      onClick={type === 'left' ? rotateLeft : rotateRight}
    >
      {type === 'left' ? <RotateLeftIcon /> : <RotateRightIcon />}
    </BaseControlButton>
  );
};

export const Close = () => {
  const { closeViewer } = useImageViewer();

  return (
    <BaseControlButton onClick={() => closeViewer()}>
      <CloseIcon />
    </BaseControlButton>
  );
};

export const Print = () => {
  const { currentImage } = useImageViewer();
  const { showErrorDialog } = useErrorDialog();
  const [isPrinting, setIsPrinting] = useState(false);

  const onPrint = async () => {
    if (!currentImage?.path) {
      return;
    }
    setIsPrinting(true);
    try {
      const { printPdf } = await import('./printPdf');
      await printPdf(currentImage.path);
    } catch (error: any) {
      showErrorDialog(error);
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <BaseControlButton
      disabled={!currentImage?.path || isPrinting}
      onClick={onPrint}
    >
      <PrintIcon />
    </BaseControlButton>
  );
};

export const OpenInNew = () => {
  const { currentImage } = useImageViewer();

  return (
    <BaseControlButton
      disabled={!currentImage?.path}
      onClick={() => {
        if (currentImage?.path) {
          window.open(currentImage.path, '_blank');
        }
      }}
    >
      <OpenInNewIcon />
    </BaseControlButton>
  );
};

export const Download = () => {
  const { currentImage } = useImageViewer();
  const { showErrorDialog } = useErrorDialog();
  const download = () => {
    if (currentImage) {
      MediaService.getInstance()
        .download(currentImage, {
          presignedUrl: true
        })
        .catch(showErrorDialog);
    }
  };
  // TODO download forbidden?
  return (
    <BaseControlButton disabled={!currentImage?.id} onClick={download}>
      <DownloadIcon />
    </BaseControlButton>
  );
};

export const Delete = () => {
  const { currentImage } = useImageViewer();
  const [deleteMedium, isDeleting] = useDeleteCurrentPatientMedium();

  const isDisabled =
    isDeleting || !currentImage?.id || !currentImage?.medicalDocument;

  return (
    <ConfirmDelete onClick={() => deleteMedium(currentImage)}>
      {({ onClick, onCancel, confirm }) => (
        <>
          {confirm && (
            <BaseControlButton onClick={onCancel} sx={{ opacity: 0.6 }}>
              <BlockIcon fontSize="small" />
            </BaseControlButton>
          )}
          <BaseControlButton
            disabled={isDisabled}
            onClick={onClick}
            color="error"
          >
            {confirm ? <CheckIcon fontSize="small" /> : <TrashIcon />}
          </BaseControlButton>
        </>
      )}
    </ConfirmDelete>
  );
};
